import { useCallback, useEffect, useState } from "react";
import { Permission, User } from "../user.entity";
import { userRepository } from "../user.repository";
import { PagenedResult } from "../../common/pagened-result";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BarChartIcon from "@mui/icons-material/BarChart";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LockIcon from "@mui/icons-material/Lock";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import HandshakeIcon from "@mui/icons-material/Handshake";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Shell } from "../../common/shell/shell.page";
import { SwalReact } from "../../common/swal-react";
import firebase from "../../core/firebase";
import { httpsCallable } from "firebase/functions";
import application from "../../core/application";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import React from "react";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

function parseCommissionPercentInput(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = parseFloat(t.replace(",", "."));
  if (Number.isNaN(n)) return null;
  return Math.min(100, Math.max(0, n));
}

type PermissionMeta = {
  label: string;
  description: string;
  Icon: React.ComponentType<{ fontSize?: "small" | "inherit" | "medium" | "large"; sx?: object }>;
};

const NON_ADMIN_PERMISSIONS: Permission[] = [
  Permission.SELL,
  Permission.READ_SALES_ANALYTICS,
  Permission.WRITE_PRODUCTS,
  Permission.READ_WRITE_PRODUCTS_PRIVATE,
  Permission.CASH_REGISTER_MANAGEMENT,
  Permission.ACCOUNTS_PAYABLE,
  Permission.MANAGE_CUSTOMERS,
  Permission.ALLOW_CREDIT_SALE,
];

const permissionMeta: Record<Permission, PermissionMeta> = {
  [Permission.SELL]: {
    label: "Vendas",
    description: "Realizar vendas e visualizar produtos",
    Icon: StorefrontIcon,
  },
  [Permission.READ_SALES_ANALYTICS]: {
    label: "Estatísticas",
    description: "Painel de relatórios e detalhes de vendas",
    Icon: BarChartIcon,
  },
  [Permission.WRITE_PRODUCTS]: {
    label: "Produtos",
    description: "Cadastrar e editar produtos no catálogo",
    Icon: Inventory2Icon,
  },
  [Permission.READ_WRITE_PRODUCTS_PRIVATE]: {
    label: "Dados privados",
    description: "Ver e editar preço de custo e margem",
    Icon: LockIcon,
  },
  [Permission.CASH_REGISTER_MANAGEMENT]: {
    label: "Caixa",
    description: "Abrir, fechar caixa e ver relatórios",
    Icon: PointOfSaleIcon,
  },
  [Permission.ACCOUNTS_PAYABLE]: {
    label: "Contas a pagar",
    description: "Gerenciar contas a pagar e vencimentos",
    Icon: ReceiptLongIcon,
  },
  [Permission.MANAGE_CUSTOMERS]: {
    label: "Clientes",
    description: "Cadastrar clientes e gerenciar fiado",
    Icon: PeopleAltIcon,
  },
  [Permission.ALLOW_CREDIT_SALE]: {
    label: "Venda fiado",
    description: "Autorizar finalizar venda no fiado",
    Icon: HandshakeIcon,
  },
  [Permission.ADMIN]: {
    label: "Administrador",
    description: "Acesso total ao sistema",
    Icon: AdminPanelSettingsIcon,
  },
};

function getEffectivePermissions(admin: boolean, sel: Set<Permission>): Permission[] {
  if (admin) return [...NON_ADMIN_PERMISSIONS, Permission.ADMIN];
  return Array.from(sel);
}

function PermissionSelector({
  defaultPermissions,
  onChange,
}: {
  defaultPermissions: Permission[];
  onChange: (perms: Permission[]) => void;
}) {
  const theme = useTheme();
  const [adminChecked, setAdminChecked] = useState(
    defaultPermissions.includes(Permission.ADMIN)
  );
  const [selected, setSelected] = useState<Set<Permission>>(
    () => new Set(defaultPermissions.filter((p) => p !== Permission.ADMIN))
  );

  useEffect(() => {
    onChange(getEffectivePermissions(adminChecked, selected));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminChecked, selected]);

  const togglePermission = (p: Permission) => {
    if (adminChecked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const toggleAdmin = () => {
    const next = !adminChecked;
    setAdminChecked(next);
    if (next) setSelected(new Set(NON_ADMIN_PERMISSIONS));
  };

  return (
    <Box sx={{ textAlign: "left", mt: 1 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: "0.1em" }}
      >
        Permissões
      </Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {NON_ADMIN_PERMISSIONS.map((p) => {
          const meta = permissionMeta[p];
          const checked = adminChecked || selected.has(p);
          return (
            <Paper
              key={p}
              elevation={0}
              onClick={() => togglePermission(p)}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: checked ? "primary.main" : "divider",
                borderRadius: 2,
                cursor: adminChecked ? "default" : "pointer",
                bgcolor: checked
                  ? alpha(theme.palette.primary.main, 0.05)
                  : "transparent",
                opacity: adminChecked ? 0.6 : 1,
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                userSelect: "none",
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: checked
                    ? "primary.main"
                    : alpha(theme.palette.text.secondary, 0.08),
                  color: checked ? "white" : "text.secondary",
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
              >
                <meta.Icon fontSize="small" />
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={checked ? "primary.main" : "text.primary"}
                >
                  {meta.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {meta.description}
                </Typography>
              </Box>
              <Checkbox
                checked={checked}
                disabled={adminChecked}
                size="small"
                color="primary"
                sx={{ p: 0.5 }}
                onClick={(e) => e.stopPropagation()}
                onChange={() => togglePermission(p)}
              />
            </Paper>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: "0.1em" }}
      >
        Acesso especial
      </Typography>
      <Paper
        elevation={0}
        onClick={toggleAdmin}
        sx={{
          mt: 1,
          p: 2,
          border: 2,
          borderColor: adminChecked ? "warning.main" : "divider",
          borderRadius: 2,
          cursor: "pointer",
          bgcolor: adminChecked
            ? alpha(theme.palette.warning.main, 0.06)
            : "transparent",
          transition: "all 0.15s",
          userSelect: "none",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: adminChecked
                ? "warning.main"
                : alpha(theme.palette.warning.main, 0.12),
              color: adminChecked ? "white" : "warning.dark",
              flexShrink: 0,
              transition: "all 0.15s",
            }}
          >
            <AdminPanelSettingsIcon />
          </Box>
          <Box flex={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body1"
                fontWeight={700}
                color={adminChecked ? "warning.dark" : "text.primary"}
              >
                Administrador do sistema
              </Typography>
              <Checkbox
                checked={adminChecked}
                size="small"
                color="warning"
                sx={{ p: 0.5 }}
                onClick={(e) => e.stopPropagation()}
                onChange={toggleAdmin}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Ativar concede <strong>acesso total ao sistema</strong>, incluindo
              gestão de usuários, configurações e todos os módulos. Todas as
              permissões são ativadas automaticamente.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

function UserCard({
  u,
  currentUserId,
  onEdit,
  onDelete,
}: {
  u: User;
  currentUserId: string | undefined;
  onEdit: (u: User) => void;
  onDelete: (u: User) => void;
}) {
  const theme = useTheme();
  const isAdmin = u.hasPermission(Permission.ADMIN);
  const isSelf = currentUserId === u.id;
  const initials = u.name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: isAdmin ? alpha(theme.palette.warning.main, 0.6) : "divider",
        borderRadius: 2,
        height: "100%",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: theme.shadows[2] },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            sx={{
              bgcolor: isAdmin ? "warning.main" : "primary.main",
              width: 48,
              height: 48,
              fontWeight: 700,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {initials || "?"}
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography fontWeight={700} variant="body1">
                {u.name}
              </Typography>
              {isAdmin && (
                <Chip
                  label="Admin"
                  size="small"
                  icon={<AdminPanelSettingsIcon />}
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                />
              )}
              {isSelf && (
                <Chip
                  label="Você"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "0.7rem" }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {u.email}
            </Typography>

            {!isAdmin && (
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
                {u.permissions.map((p) => {
                  const meta = permissionMeta[p];
                  if (!meta) return null;
                  return (
                    <Chip
                      key={p}
                      label={meta.label}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem", color: "text.secondary", borderColor: "divider" }}
                    />
                  );
                })}
              </Stack>
            )}

            {u.commissionPercent != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.75, display: "block" }}
              >
                Comissão personalizada: {u.commissionPercent}%
              </Typography>
            )}
          </Box>

          <Stack direction="column" spacing={0.5} flexShrink={0}>
            <Tooltip title="Editar">
              <IconButton size="small" color="primary" onClick={() => onEdit(u)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isSelf && (
              <Tooltip title="Remover">
                <IconButton size="small" color="error" onClick={() => onDelete(u)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export const UsersPage = () => {
  application.useHasPermission(Permission.ADMIN);
  const theme = useTheme();
  const [usersPagened, setUsersPagened] = useState<PagenedResult<User>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevius, setHasPrevius] = useState<boolean>(true);
  const [user] = application.user.useState();
  const moduleAccess = useModuleAccess();

  useEffect(() => {
    (async () => {
      const users = await userRepository.getAllPagened(10, "name");
      setUsersPagened(users);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!usersPagened) return;
      setHasNext(await usersPagened.hasNext());
      setHasPrevius(await usersPagened.hasPrevius());
    })();
  }, [usersPagened]);

  const nextPage = useCallback(async () => {
    if (!usersPagened) return;
    setUsersPagened(await usersPagened.next());
  }, [usersPagened]);

  const previusPage = useCallback(async () => {
    if (!usersPagened) return;
    setUsersPagened(await usersPagened.previus());
  }, [usersPagened]);

  const editUser = async (userToEdit: User) => {
    const permissionsRef = { current: userToEdit.permissions };

    SwalReact.fire({
      title: "Editar usuário",
      html: (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            id="edit-user-name"
            variant="outlined"
            label="Nome"
            defaultValue={userToEdit.name}
            fullWidth
          />
          <TextField
            id="edit-user-commission"
            variant="outlined"
            label="Comissão personalizada (%)"
            placeholder="Em branco = percentual global"
            defaultValue={
              userToEdit.commissionPercent != null
                ? String(userToEdit.commissionPercent)
                : ""
            }
            fullWidth
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            helperText="Opcional. Sobre as vendas deste usuário."
          />
          <PermissionSelector
            defaultPermissions={userToEdit.permissions}
            onChange={(perms) => { permissionsRef.current = perms; }}
          />
        </Stack>
      ),
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: async () => {
        if (!user) return;
        const name = (document.getElementById("edit-user-name") as HTMLInputElement)?.value?.trim();
        const commissionRaw = (document.getElementById("edit-user-commission") as HTMLInputElement)?.value ?? "";
        const userPermissions = permissionsRef.current;

        if (!name) {
          return SwalReact.showValidationMessage("Preencha o nome do usuário.");
        }

        if (user.id === userToEdit.id && !userPermissions.includes(Permission.ADMIN)) {
          return SwalReact.showValidationMessage(
            "Você não pode remover o acesso de administrador do seu próprio usuário."
          );
        }

        if (
          user.id !== userToEdit.id &&
          userPermissions.includes(Permission.ADMIN) &&
          !userToEdit.permissions.includes(Permission.ADMIN)
        ) {
          const { isConfirmed } = await SwalReact.fire({
            title: "Atenção",
            text: "Confirma conceder acesso de administrador? Este usuário poderá alterar permissões de outros.",
            showCancelButton: true,
            icon: "warning",
            cancelButtonText: "Cancelar",
            confirmButtonText: "Sim, confirmar",
          });
          if (!isConfirmed) {
            SwalReact.fire({ title: "Operação cancelada", icon: "info" });
            return;
          }
        }

        userToEdit.name = name;
        userToEdit.permissions = userPermissions;
        userToEdit.commissionPercent = parseCommissionPercentInput(commissionRaw);
        await userRepository.save(userToEdit);
        setUsersPagened(await usersPagened?.refreshItens());
        return true;
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });
  };

  const createUser = async () => {
    const usersLimit = moduleAccess.limits.usersLimit;
    const totalUsers = usersPagened?.itens.length ?? 0;
    if (Number.isFinite(usersLimit) && totalUsers >= usersLimit) {
      await SwalReact.fire({
        title: "Limite do plano atingido",
        text: "Seu plano atingiu o limite de usuários. Faça upgrade para adicionar mais pessoas.",
        icon: "warning",
      });
      return;
    }

    const permissionsRef = { current: [] as Permission[] };

    SwalReact.fire({
      title: "Novo usuário",
      html: (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            id="new-user-name"
            variant="outlined"
            label="Nome"
            fullWidth
            autoFocus
          />
          <TextField
            id="new-user-email"
            variant="outlined"
            label="Email"
            fullWidth
            type="email"
          />
          <TextField
            id="new-user-password"
            variant="outlined"
            label="Senha"
            fullWidth
            type="password"
          />
          <TextField
            id="new-user-commission"
            variant="outlined"
            label="Comissão personalizada (%)"
            placeholder="Em branco = percentual global"
            fullWidth
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            helperText="Opcional. Sobre as vendas deste usuário."
          />
          <PermissionSelector
            defaultPermissions={[]}
            onChange={(perms) => { permissionsRef.current = perms; }}
          />
        </Stack>
      ),
      showCancelButton: true,
      confirmButtonText: "Criar usuário",
      cancelButtonText: "Cancelar",
      preConfirm: async () => {
        const name = (document.getElementById("new-user-name") as HTMLInputElement)?.value?.trim();
        const email = (document.getElementById("new-user-email") as HTMLInputElement)?.value?.trim();
        const password = (document.getElementById("new-user-password") as HTMLInputElement)?.value?.trim();
        const commissionRaw = (document.getElementById("new-user-commission") as HTMLInputElement)?.value ?? "";
        const userPermissions = permissionsRef.current;

        if (!name || !email || !password) {
          return SwalReact.showValidationMessage("Preencha nome, email e senha.");
        }
        if (password.length < 6) {
          return SwalReact.showValidationMessage("A senha deve ter no mínimo 6 caracteres.");
        }
        if (userPermissions.length === 0) {
          return SwalReact.showValidationMessage("Selecione ao menos uma permissão.");
        }

        if (userPermissions.includes(Permission.ADMIN)) {
          const { isConfirmed } = await SwalReact.fire({
            title: "Atenção",
            text: "Confirma criar este usuário como administrador? Ele terá acesso total ao sistema.",
            showCancelButton: true,
            icon: "warning",
            cancelButtonText: "Cancelar",
            confirmButtonText: "Sim, criar",
          });
          if (!isConfirmed) {
            SwalReact.fire({ title: "Operação cancelada", icon: "info" });
            return;
          }
        }

        try {
          const commissionPercent = parseCommissionPercentInput(commissionRaw);
          const createPayload: Record<string, unknown> = {
            name,
            email,
            password,
            permissions: userPermissions,
          };
          if (commissionPercent !== null) {
            createPayload.commissionPercent = commissionPercent;
          }

          const result = (await httpsCallable(
            firebase.functions,
            "createUser"
          )(createPayload)) as { data: { code?: number; error?: string } };

          if (result.data.code !== 200) {
            if (result.data.error === "EMAIL_ALREADY_EXISTS") {
              return SwalReact.showValidationMessage("Já existe um usuário com este email.");
            }
            SwalReact.fire({ title: "Ocorreu um erro, tente novamente mais tarde.", icon: "error" });
            return;
          }
        } catch {
          return SwalReact.fire({ title: "Ocorreu um erro, tente novamente mais tarde.", icon: "error" });
        }

        setUsersPagened(await usersPagened?.refreshItens());
        return true;
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });
  };

  const removeUser = async (userToRemove: User) => {
    await SwalReact.fire({
      title: "Remover usuário",
      text: `Remover ${userToRemove.name} permanentemente?`,
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      icon: "warning",
      preConfirm: async () => {
        try {
          const result = (await httpsCallable(
            firebase.functions,
            "removeUser"
          )({ uid: userToRemove.id })) as { data: { code?: number; error?: string } };

          if (result.data.code !== 200) {
            return SwalReact.showValidationMessage("Erro ao remover usuário. Tente novamente.");
          }
        } catch {
          return SwalReact.showValidationMessage("Erro ao remover usuário. Tente novamente.");
        }
        setUsersPagened(await usersPagened?.refreshItens());
        return true;
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });
  };

  if (loading || !usersPagened || moduleAccess.loading) {
    return (
      <Shell title="Usuários">
        <LoadingOverlay message="Carregando usuários…" subtle />
      </Shell>
    );
  }

  const usersEnabled =
    moduleAccess.isEnabled("users_basic") ||
    moduleAccess.isEnabled("users_advanced") ||
    moduleAccess.isEnabled("users_unlimited");
  const usersLimit = moduleAccess.limits.usersLimit;
  const totalUsers = usersPagened.itens.length;
  const atLimit = Number.isFinite(usersLimit) && totalUsers >= usersLimit;

  if (!usersEnabled) {
    return (
      <Shell title="Usuários">
        <PageHeader title="Usuários" description="Controle de acesso ao sistema." />
        <ModuleLockedView
          moduleName="Controle de usuários"
          groupId="users_control"
          details="Ative uma variação do módulo de usuários para gerenciar acessos da equipe."
          features={[
            "Convite de colaboradores",
            "Permissões por função",
            "Limite de usuários por plano",
          ]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Usuários">
      <PageHeader
        title="Usuários"
        description="Controle quem acessa o sistema e o que cada colaborador pode fazer."
        action={
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => createUser()}
          >
            Novo usuário
          </Button>
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            opacity: 0.04,
            pointerEvents: "none",
          }}
        >
          <GroupIcon sx={{ fontSize: 180 }} />
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              flexShrink: 0,
            }}
          >
            <GroupIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Gestão de equipe
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure permissões individuais para cada colaborador.
            </Typography>
          </Box>
          <Chip
            icon={<GroupIcon />}
            label={
              Number.isFinite(usersLimit)
                ? `${totalUsers} de ${usersLimit} usuários`
                : `${totalUsers} usuário${totalUsers !== 1 ? "s" : ""}`
            }
            color={atLimit ? "warning" : "default"}
            variant={atLimit ? "filled" : "outlined"}
          />
        </Stack>
      </Paper>

      {atLimit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Limite de usuários atingido. Faça upgrade do módulo para liberar mais acessos.
        </Alert>
      )}

      <Grid container spacing={2}>
        {usersPagened.itens.map((u) => (
          <Grid item xs={12} md={6} key={u.id}>
            <UserCard
              u={u}
              currentUserId={user?.id}
              onEdit={editUser}
              onDelete={removeUser}
            />
          </Grid>
        ))}
      </Grid>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
        <Button variant="outlined" disabled={hasPrevius} onClick={previusPage} fullWidth>
          Anterior
        </Button>
        <Button variant="outlined" disabled={hasNext} onClick={nextPage} fullWidth>
          Próxima
        </Button>
      </Stack>
    </Shell>
  );
};
